/**
 * 🎬 **한 장에서 움직임 만들기** — 걷는 그림 하나로 걷기 네 칸을 굽는다(결정론적).
 *
 * ## 왜 AI 로 안 뽑나
 * 걷기 한 방향은 네 칸이 가로로 이어진 그림이다. 이미지 모델은 매 장 얼굴·옷·비율이
 * 조금씩 달라서, 네 칸이 미세하게 다른 아이가 되면 걸을 때 **덜덜 떨린다.**
 * 참고한 `sprite-gen`(Apache-2.0) 도 자기 문서에 그렇게 적어 두었다 —
 * 「주기적 이동 동작(walk/run)은 모션 QA를 통과하지 않는 한 **실험적**」.
 *
 * ## 무엇을 움직이나 — 걸음의 뼈대는 셋이다
 * 처음엔 몸통을 눌렀다 펴는 것 하나뿐이었다. 그러니 **네 칸이 거의 같아 보였다**
 * (게다가 1·3번째 칸이 값까지 똑같아서, 사실은 두 칸짜리 깜빡임이었다).
 * 걸음처럼 보이려면 셋이 같이 가야 한다:
 *  · **무릎 굽힘** — 발이 땅에 닿을 때 엉덩이가 내려가고, 머리·몸통이 통째로 따라 내려간다
 *  · **다리 흔들림** — 엉덩이는 그대로 두고 **발끝만** 앞뒤로 민다(한 장에서 두 다리를
 *    갈라낼 수는 없으니, 두 다리가 같이 흔들리는 근사다 — 그래도 「벌어졌다 모였다」가 보인다)
 *  · **위아래 튐** — 두 발이 모이는 순간 몸이 가장 높다
 *
 * ## 네 칸은 걸음 한 바퀴다
 * `접지A(다리 벌어짐·낮음) → 지나감(모임·높음) → 접지B(반대로 벌어짐) → 지나감'`.
 * 🔴 **네 칸이 모두 달라야 한다.** 같은 값이 두 번 들어가면 8fps 가 4fps 가 되고,
 * 「걷는다」가 아니라 「떤다」로 보인다 — 실제로 그렇게 만들어 놓고 한참 몰랐다.
 *
 * ## 얼굴은 절대 안 변한다
 * 머리는 크기를 안 바꾸고 통째로 옮기기만 한다(눌리면 딴사람이 된다). 같은 그림이니
 * 얼굴·옷이 네 칸 내내 똑같다. 공짜고, 즉시고, 매번 같은 결과다.
 *
 * ## 여기서 하는 일은 「재기」뿐
 * 픽셀을 실제로 옮기는 건 캔버스가 한다(`apps/admin/src/lib/bakeWalk.ts`).
 * 재는 규칙만 여기 두는 이유: 이건 **눈으로 확인하기 어려운 판정**이라 테스트로 묶어 둬야 한다.
 */

/** 가로 한 줄에 불투명 픽셀이 몇 개인가 — 위에서 아래로. 캔버스가 재서 넘긴다. */
export type RowCoverage = readonly number[];

export interface Silhouette {
  /** 그림이 시작하는 줄(맨 위 불투명). */
  topRow: number;
  /** 발이 닿는 줄(맨 아래 불투명). 여기를 고정해야 걷는 것처럼 보인다. */
  groundRow: number;
  /** 목 — 머리와 몸통을 가르는 줄. 여기 위는 크기를 안 건드린다. */
  neckRow: number;
  /** 엉덩이 — 몸통과 다리를 가르는 줄. 무릎 굽힘·다리 흔들림의 축이다. */
  hipRow: number;
  /** 그림의 세로 길이(topRow~groundRow). */
  height: number;
}

/**
 * 실루엣을 잰다. **빈 그림에도 죽지 않는다** — 못 재면 `null`, 부르는 쪽이 그대로 둔다.
 *
 * 목 찾기: 머리는 위쪽에서 가장 넓어지는 자리다. 그 아래로 내려가며 **가장 좁아지는 줄**이 목이다.
 * 눈사람처럼 목이 없는 그림도 있어서, 못 찾으면 위에서 30% 지점으로 둔다 —
 * 「정확한 목」이 아니라 **머리를 안 건드릴 경계**가 필요한 것이라 그 정도면 된다.
 */
export function analyzeSilhouette(rows: RowCoverage): Silhouette | null {
  let topRow = -1;
  let groundRow = -1;
  for (let y = 0; y < rows.length; y++) {
    if ((rows[y] ?? 0) > 0) {
      if (topRow < 0) topRow = y;
      groundRow = y;
    }
  }
  if (topRow < 0 || groundRow <= topRow) return null;

  const height = groundRow - topRow;
  const fallback = topRow + Math.round(height * 0.3);

  /**
   * 🔴 **머리에서 가장 넓은 줄을 먼저 찾으면 안 된다.** 사람은 어깨·몸통이 머리보다 넓어서,
   * 찾는 범위가 조금만 넓어도 **몸통을 머리로 착각**한다(처음에 그렇게 짜서 목이 37로 잡혔다).
   *
   * 대신 위에서 아래로 훑으며 **여태 본 가장 넓은 폭**을 들고 간다. 그 폭보다 확 좁아지는
   * 첫 자리가 목이다 — 머리를 다 지난 뒤에야 그런 자리가 나온다.
   */
  const from = topRow + Math.max(1, Math.round(height * 0.1)); // 정수리 근처는 원래 좁다
  const to = Math.min(topRow + Math.max(2, Math.round(height * 0.55)), rows.length - 1);
  let seenMax = 0;
  for (let y = topRow; y < from; y++) seenMax = Math.max(seenMax, rows[y] ?? 0);

  let neck = -1;
  for (let y = from; y <= to; y++) {
    const w = rows[y] ?? 0;
    // 여태 본 폭의 85% 미만으로 잘록해지는 **첫** 자리. 그 뒤 어깨에서 다시 넓어진다.
    if (neck < 0 && seenMax > 0 && w < seenMax * 0.85) neck = y;
    seenMax = Math.max(seenMax, w);
  }
  // 잘록함이 없으면(눈사람·병아리 같은 덩어리) 기본 자리로.
  const neckRow = neck >= 0 ? neck : fallback;

  /**
   * 엉덩이는 **재지 않고 비율로 잡는다.**
   *
   * 실루엣만으로 허리를 찾으려 하면 옷·가방·팔에 걸려 엉뚱한 줄이 잡힌다(목을 찾다가
   * 몸통에 걸렸던 것과 같은 함정이다). 게다가 여기 필요한 건 「해부학적으로 정확한 골반」이
   * 아니라 **다리를 흔들 축**이다 — 목과 발의 가운데면 어느 그림에서나 다리 위쪽에 온다.
   */
  const wanted = neckRow + Math.round((groundRow - neckRow) * 0.5);
  // 아주 납작한 그림(두어 줄짜리)에서는 목·엉덩이·바닥이 겹칠 수 있다 — 순서만 지키고 죽지 않는다.
  const hipRow = Math.max(neckRow, Math.min(groundRow - 1, wanted));

  return { topRow, groundRow, neckRow, hipRow, height };
}

/**
 * 한 칸의 자세. 값은 **그림 세로 길이에 대한 비율**이라 어떤 크기의 그림에도 같게 보인다.
 *
 * - `squash` : 무릎을 굽히는 정도. 엉덩이가 내려가고 머리·몸통이 따라 내려간다.
 * - `lift`   : 그림 전체를 위로 띄우는 정도. 두 발이 모일 때 몸이 가장 높다.
 * - `stride` : 발끝을 앞뒤로 미는 정도(−1~+1). 엉덩이는 안 움직인다.
 */
export interface WalkPose {
  squash: number;
  lift: number;
  /**
   * 발을 축으로 몸 전체를 기울이는 각도(도). 걸음의 좌우 흔들림.
   *
   * 🔴 **작아야 한다.** 예전 방식(`make-walk-strips.mjs`)은 ±4° 를 **유일한 움직임**으로 썼고,
   * 그래서 캐릭터가 걷는 게 아니라 **넘어지는 것처럼** 보였다. 다른 움직임과 같이 쓰면
   * ±1.5° 로도 충분하다.
   */
  lean?: number;
  /** 다리 흔들림(−1~+1). 0 이면 두 발이 모인 자세. */
  stride?: number;
}

/** 발끝이 밀리는 최대 — 그림 세로 길이에 대한 비율. 이보다 크면 다리가 고무처럼 늘어난다. */
export const STRIDE_MAX = 0.055;

/**
 * 걷기 네 칸 = **걸음 한 바퀴.**
 *
 * 🔴 네 칸이 **모두 다르다.** 예전엔 1·3번째 칸이 같은 값(`squash:0, lift:0`)이라
 * 사실상 두 칸짜리였고, 그래서 「거의 같은 그림이 깜빡인다」로 보였다.
 *
 * 값이 작은 이유는 그대로다 — 그림책 캐릭터는 조금만 움직여야 귀엽다.
 */
export const WALK_POSES: readonly WalkPose[] = [
  // 접지 A — 앞발이 땅에 닿는다. 다리가 벌어지고 무릎이 굽어 몸이 가장 낮다.
  { squash: 0.055, lift: 0, lean: 1.2, stride: 1 },
  // 지나감 — 두 발이 스치듯 모이고 몸이 가장 높다.
  { squash: 0, lift: 0.045, lean: 0, stride: 0 },
  // 접지 B — 반대 발. 기울기도 반대로.
  { squash: 0.055, lift: 0, lean: -1.2, stride: -1 },
  // 지나감′ — 같은 자세를 그대로 되풀이하지 않는다(네 칸이 다 달라야 한다).
  { squash: 0.012, lift: 0.032, lean: 0, stride: 0 },
];

/** 제자리 숨쉬기(걷지 않는 캐릭터·정지 화면용). 더 작고, 다리는 안 흔든다. */
export const BREATHE_POSES: readonly WalkPose[] = [
  { squash: 0, lift: 0 },
  { squash: 0.018, lift: 0 },
  { squash: 0.026, lift: 0 },
  { squash: 0.018, lift: 0 },
];

/** 한 칸을 실제로 그릴 때 필요한 **픽셀 단위** 값. 캔버스가 이대로 그리면 된다. */
export interface BakeStep {
  /** 무릎이 굽어 엉덩이가 내려가는 픽셀. 머리+몸통이 통째로 이만큼 내려간다. */
  dy: number;
  /** 머리+몸통 구간(원본). **크기를 안 바꾼다** — 얼굴이 눌리면 딴사람이 된다. */
  upperFrom: { top: number; bottom: number };
  /** 다리 구간(원본). */
  legFrom: { top: number; bottom: number };
  /** 다리를 그릴 자리. 아래는 **늘 바닥선**이다. */
  legTo: { top: number; bottom: number };
  /**
   * 다리를 가로로 넓히는 비율(1 = 그대로).
   *
   * 🔴 **눌리면 옆으로 퍼져야 한다.** 세로로만 줄이면 부피가 사라져서 몸이 작아진 것처럼
   * 보인다 — 걷는 게 아니라 **멀어지는** 것으로 읽힌다.
   */
  legScaleX: number;
  /** 발끝이 옆으로 밀리는 픽셀(+오른쪽). 엉덩이 쪽은 0 — 거기서부터 비스듬히 밀린다. */
  legShear: number;
  /** 발을 축으로 기울이는 각도(도). 0 이면 안 기울인다. */
  lean: number;
  /** 그림 전체를 위로 띄우는 픽셀. */
  lift: number;
}

/**
 * 가로로 펼 수 있는 최대.
 *
 * 예전엔 1.06 이었다. **몸통 전체**를 폈기 때문인데 — 그때는 팔이 프레임 밖으로 나갔다.
 * 지금 펴는 건 **다리뿐**이고 다리는 가운데에 모여 있어서, 가운데 기준으로 늘려도
 * 실제로 움직이는 픽셀은 몇 px 뿐이다. 상한이 너무 낮으면 오히려 **부피가 안 지켜진다**
 * (다리가 짧을수록 같은 굽힘에도 비율이 커진다 — 실제 값에서 상한에 걸렸다).
 */
const MAX_WIDEN = 1.1;

/**
 * 자세 하나를 픽셀 값으로 바꾼다.
 *
 * 🔴 **바닥선은 안 움직인다.** 무릎을 굽혀도 발이 그 자리에 있어야 걷는 것으로 보인다 —
 * 위아래 가운데를 기준으로 줄이면 발이 공중에 뜨고, 그건 걷기가 아니라 떠다니기다.
 * (띄우기(`lift`)는 그 위에 얹는 별개의 값이다 — 두 발이 모이는 순간의 튐.)
 */
export function bakeStep(anat: Silhouette, pose: WalkPose): BakeStep {
  const legH = Math.max(1, anat.groundRow - anat.hipRow);
  // 굽힌 만큼 다리가 짧아진다. 1px 미만은 반올림해서 0 — 안 움직이는 칸이 생기는 게 맞다.
  const bend = Math.round(legH * pose.squash);
  const newH = Math.max(1, legH - bend);
  const dy = legH - newH; // 반올림·하한 때문에 굽힘과 다를 수 있다. 실제로 줄어든 만큼만 내린다.
  return {
    dy,
    upperFrom: { top: anat.topRow, bottom: anat.hipRow },
    legFrom: { top: anat.hipRow, bottom: anat.groundRow },
    legTo: { top: anat.groundRow - newH, bottom: anat.groundRow },
    // 부피를 지킨다 — 세로로 줄어든 만큼 가로로 편다(고전적인 스쿼시 앤 스트레치).
    legScaleX: Math.min(legH / newH, MAX_WIDEN),
    legShear: Math.round(anat.height * STRIDE_MAX * (pose.stride ?? 0)),
    lean: pose.lean ?? 0,
    lift: Math.round(anat.height * pose.lift),
  };
}
